import { auth } from './firebase';

/**
 * Get the current user's ID token for API authentication
 */
async function getIdToken(): Promise<string | null> {
  try {
    const user = auth.currentUser;
    if (!user) {
      return null;
    }
    return await user.getIdToken();
  } catch (error) {
    console.error('Failed to get ID token:', error);
    return null;
  }
}

export interface Transaction {
  date: string;
  description: string;
  amount: string;
  payee: string;
  exchangeRate?: string; // Optional, for converters that track exchange rates
  category?: string; // Optional, for categorized transactions (legacy, use categoryId instead)
  categoryId?: string; // Optional, Actual Budget category ID
  categoryName?: string; // Optional, human-readable category name
  confidence?: number; // Optional, confidence score for categorization
}

/**
 * Escape CSV field if it contains commas, quotes, or newlines
 */
export function escapeCSVField(field: string): string {
  if (!field) return '';
  
  if (field.includes(',') || field.includes('"') || field.includes('\n')) {
    // Escape quotes by doubling them and wrap the field in quotes
    return `"${field.replace(/"/g, '""')}"`;
  }
  return field;
}

/**
 * Generate CSV content from array of transactions
 */
export async function generateCSV(
  transactions: Transaction[], 
  targetCurrency: string,
  includeExchangeRate: boolean = false,
  categorizeTransactions: boolean = false,
  budgetConfig?: {
    serverURL: string;
    password: string;
    budgetId: string;
    budgetPassword?: string;
  }
): Promise<string> {
  let processedTransactions = transactions;
  
  // If categorization is requested and budget config is provided
  if (categorizeTransactions && budgetConfig) {
    try {
      // Get authentication token
      const token = await getIdToken();
      if (!token) {
        throw new Error('Authentication required to use transaction categorization');
      }

      const API_BASE_URL = process.env.REACT_APP_API_URL || 
        'https://us-central1-cms-demo-daed0.cloudfunctions.net/api';
      
      const response = await fetch(`${API_BASE_URL}/categorize-transactions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          transactions,
          ...budgetConfig
        }),
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          processedTransactions = result.transactions;
        }
      } else {
        console.warn('Failed to categorize transactions:', await response.text());
      }
    } catch (error) {
      console.warn('Error categorizing transactions:', error);
    }
  }
  
  const exchangeRateHeader = includeExchangeRate ? `,Exchange Rate (1 ${targetCurrency} = X source)` : '';
  const categoryHeader = categorizeTransactions ? ',Category,CategoryId' : '';
  const csvHeader = `Date,Description,Amount (${targetCurrency}),Payee${exchangeRateHeader}${categoryHeader}\n`;
  
  const csvRows = processedTransactions.map(transaction => {
    const escapedDescription = escapeCSVField(transaction.description);
    const escapedPayee = escapeCSVField(transaction.payee);
    const exchangeRateField = includeExchangeRate && transaction.exchangeRate ? `,${transaction.exchangeRate}` : '';
    const categoryField = categorizeTransactions ? `,${escapeCSVField(transaction.categoryName || 'Uncategorized')},${transaction.categoryId || ''}` : '';
    return `${transaction.date},${escapedDescription},${transaction.amount},${escapedPayee}${exchangeRateField}${categoryField}`;
  }).join('\n');
  
  return csvHeader + csvRows;
}

/**
 * Download a CSV file
 */
export function downloadCSV(csvContent: string, filename: string) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
} 

// Comprehensive currency mapping with display names and emojis
export const CURRENCY_INFO: Record<string, { name: string; symbol?: string; emoji?: string }> = {
  '1INCH': { name: '1inch', symbol: '1INCH', emoji: '🔗' },
  'AAVE': { name: 'Aave', symbol: 'AAVE', emoji: '👻' },
  'ADA': { name: 'Cardano', symbol: 'ADA', emoji: '🔵' },
  'AED': { name: 'UAE Dirham', symbol: 'د.إ', emoji: '🇦🇪' },
  'AFN': { name: 'Afghan Afghani', symbol: '؋', emoji: '🇦🇫' },
  'AGIX': { name: 'SingularityNET', symbol: 'AGIX', emoji: '🤖' },
  'AKT': { name: 'Akash Network', symbol: 'AKT', emoji: '☁️' },
  'ALGO': { name: 'Algorand', symbol: 'ALGO', emoji: '🔺' },
  'ALL': { name: 'Albanian Lek', symbol: 'L', emoji: '🇦🇱' },
  'AMD': { name: 'Armenian Dram', symbol: '֏', emoji: '🇦🇲' },
  'AMP': { name: 'Amp', symbol: 'AMP', emoji: '⚡' },
  'ANG': { name: 'Netherlands Antillean Guilder', symbol: 'ƒ', emoji: '🇨🇼' },
  'AOA': { name: 'Angolan Kwanza', symbol: 'Kz', emoji: '🇦🇴' },
  'APE': { name: 'ApeCoin', symbol: 'APE', emoji: '🦍' },
  'APT': { name: 'Aptos', symbol: 'APT', emoji: '🏠' },
  'AR': { name: 'Arweave', symbol: 'AR', emoji: '📦' },
  'ARB': { name: 'Arbitrum', symbol: 'ARB', emoji: '🔷' },
  'ARS': { name: 'Argentine Peso', symbol: '$', emoji: '🇦🇷' },
  'ATOM': { name: 'Cosmos', symbol: 'ATOM', emoji: '⚛️' },
  'ATS': { name: 'Austrian Schilling', symbol: 'S', emoji: '🇦🇹' },
  'AUD': { name: 'Australian Dollar', symbol: '$', emoji: '🇦🇺' },
  'AVAX': { name: 'Avalanche', symbol: 'AVAX', emoji: '🏔️' },
  'AWG': { name: 'Aruban Florin', symbol: 'ƒ', emoji: '🇦🇼' },
  'AXS': { name: 'Axie Infinity', symbol: 'AXS', emoji: '🎮' },
  'AZM': { name: 'Azerbaijani Manat (1993-2006)', symbol: 'AZM', emoji: '🇦🇿' },
  'AZN': { name: 'Azerbaijani Manat', symbol: '₼', emoji: '🇦🇿' },
  'BAKE': { name: 'BakeryToken', symbol: 'BAKE', emoji: '🥖' },
  'BAM': { name: 'Bosnia-Herzegovina Convertible Mark', symbol: 'KM', emoji: '🇧🇦' },
  'BAT': { name: 'Basic Attention Token', symbol: 'BAT', emoji: '🦇' },
  'BBD': { name: 'Barbadian Dollar', symbol: '$', emoji: '🇧🇧' },
  'BCH': { name: 'Bitcoin Cash', symbol: 'BCH', emoji: '💚' },
  'BDT': { name: 'Bangladeshi Taka', symbol: '৳', emoji: '🇧🇩' },
  'BEF': { name: 'Belgian Franc', symbol: 'F', emoji: '🇧🇪' },
  'BGN': { name: 'Bulgarian Lev', symbol: 'лв', emoji: '🇧🇬' },
  'BHD': { name: 'Bahraini Dinar', symbol: '.د.ب', emoji: '🇧🇭' },
  'BIF': { name: 'Burundian Franc', symbol: 'Fr', emoji: '🇧🇮' },
  'BMD': { name: 'Bermudan Dollar', symbol: '$', emoji: '🇧🇲' },
  'BNB': { name: 'BNB', symbol: 'BNB', emoji: '🟡' },
  'BND': { name: 'Brunei Dollar', symbol: '$', emoji: '🇧🇳' },
  'BOB': { name: 'Bolivian Boliviano', symbol: 'Bs.', emoji: '🇧🇴' },
  'BRL': { name: 'Brazilian Real', symbol: 'R$', emoji: '🇧🇷' },
  'BSD': { name: 'Bahamian Dollar', symbol: '$', emoji: '🇧🇸' },
  'BSV': { name: 'Bitcoin SV', symbol: 'BSV', emoji: '💎' },
  'BSW': { name: 'Biswap', symbol: 'BSW', emoji: '🔄' },
  'BTC': { name: 'Bitcoin', symbol: '₿', emoji: '🟠' },
  'BTCB': { name: 'Bitcoin BEP2', symbol: 'BTCB', emoji: '🟡' },
  'BTG': { name: 'Bitcoin Gold', symbol: 'BTG', emoji: '🥇' },
  'BTN': { name: 'Bhutanese Ngultrum', symbol: 'Nu.', emoji: '🇧🇹' },
  'BTT': { name: 'BitTorrent', symbol: 'BTT', emoji: '🌐' },
  'BUSD': { name: 'Binance USD', symbol: 'BUSD', emoji: '💛' },
  'BWP': { name: 'Botswanan Pula', symbol: 'P', emoji: '🇧🇼' },
  'BYN': { name: 'Belarusian Ruble', symbol: 'Br', emoji: '🇧🇾' },
  'BYR': { name: 'Belarusian Ruble (2000-2016)', symbol: 'Br', emoji: '🇧🇾' },
  'BZD': { name: 'Belize Dollar', symbol: '$', emoji: '🇧🇿' },
  'CAD': { name: 'Canadian Dollar', symbol: '$', emoji: '🇨🇦' },
  'CAKE': { name: 'PancakeSwap', symbol: 'CAKE', emoji: '🥞' },
  'CDF': { name: 'Congolese Franc', symbol: 'Fr', emoji: '🇨🇩' },
  'CELO': { name: 'Celo', symbol: 'CELO', emoji: '🌍' },
  'CFX': { name: 'Conflux', symbol: 'CFX', emoji: '🌊' },
  'CHF': { name: 'Swiss Franc', symbol: 'Fr', emoji: '🇨🇭' },
  'CHZ': { name: 'Chiliz', symbol: 'CHZ', emoji: '⚽' },
  'CLP': { name: 'Chilean Peso', symbol: '$', emoji: '🇨🇱' },
  'CNH': { name: 'Chinese Yuan (Offshore)', symbol: '¥', emoji: '🇨🇳' },
  'CNY': { name: 'Chinese Yuan', symbol: '¥', emoji: '🇨🇳' },
  'COMP': { name: 'Compound', symbol: 'COMP', emoji: '🏦' },
  'COP': { name: 'Colombian Peso', symbol: '$', emoji: '🇨🇴' },
  'CRC': { name: 'Costa Rican Colon', symbol: '₡', emoji: '🇨🇷' },
  'CRO': { name: 'Cronos', symbol: 'CRO', emoji: '👑' },
  'CRV': { name: 'Curve DAO Token', symbol: 'CRV', emoji: '📈' },
  'CSPR': { name: 'Casper', symbol: 'CSPR', emoji: '👻' },
  'CUC': { name: 'Cuban Convertible Peso', symbol: '$', emoji: '🇨🇺' },
  'CUP': { name: 'Cuban Peso', symbol: '$', emoji: '🇨🇺' },
  'CVE': { name: 'Cape Verdean Escudo', symbol: '$', emoji: '🇨🇻' },
  'CVX': { name: 'Convex Finance', symbol: 'CVX', emoji: '🔗' },
  'CYP': { name: 'Cypriot Pound', symbol: '£', emoji: '🇨🇾' },
  'CZK': { name: 'Czech Koruna', symbol: 'Kč', emoji: '🇨🇿' },
  'DAI': { name: 'Dai', symbol: 'DAI', emoji: '🟢' },
  'DASH': { name: 'Dash', symbol: 'DASH', emoji: '💨' },
  'DCR': { name: 'Decred', symbol: 'DCR', emoji: '🗳️' },
  'DEM': { name: 'German Mark', symbol: 'DM', emoji: '🇩🇪' },
  'DFI': { name: 'DeFiChain', symbol: 'DFI', emoji: '🔗' },
  'DJF': { name: 'Djiboutian Franc', symbol: 'Fr', emoji: '🇩🇯' },
  'DKK': { name: 'Danish Krone', symbol: 'kr', emoji: '🇩🇰' },
  'DOGE': { name: 'Dogecoin', symbol: 'DOGE', emoji: '🐕' },
  'DOP': { name: 'Dominican Peso', symbol: '$', emoji: '🇩🇴' },
  'DOT': { name: 'Polkadot', symbol: 'DOT', emoji: '🟣' },
  'DYDX': { name: 'dYdX', symbol: 'DYDX', emoji: '📊' },
  'DZD': { name: 'Algerian Dinar', symbol: 'د.ج', emoji: '🇩🇿' },
  'EEK': { name: 'Estonian Kroon', symbol: 'kr', emoji: '🇪🇪' },
  'EGLD': { name: 'MultiversX', symbol: 'EGLD', emoji: '⚡' },
  'EGP': { name: 'Egyptian Pound', symbol: '£', emoji: '🇪🇬' },
  'ENJ': { name: 'Enjin Coin', symbol: 'ENJ', emoji: '🎮' },
  'EOS': { name: 'EOS', symbol: 'EOS', emoji: '🔺' },
  'ERN': { name: 'Eritrean Nakfa', symbol: 'Nfk', emoji: '🇪🇷' },
  'ESP': { name: 'Spanish Peseta', symbol: '₧', emoji: '🇪🇸' },
  'ETB': { name: 'Ethiopian Birr', symbol: 'Br', emoji: '🇪🇹' },
  'ETC': { name: 'Ethereum Classic', symbol: 'ETC', emoji: '🟢' },
  'ETH': { name: 'Ethereum', symbol: 'Ξ', emoji: '🔷' },
  'EUR': { name: 'Euro', symbol: '€', emoji: '🇪🇺' },
  'EURC': { name: 'Euro Coin', symbol: 'EURC', emoji: '🇪🇺' },
  'FEI': { name: 'Fei USD', symbol: 'FEI', emoji: '🌿' },
  'FIL': { name: 'Filecoin', symbol: 'FIL', emoji: '📁' },
  'FIM': { name: 'Finnish Markka', symbol: 'mk', emoji: '🇫🇮' },
  'FJD': { name: 'Fijian Dollar', symbol: '$', emoji: '🇫🇯' },
  'FKP': { name: 'Falkland Islands Pound', symbol: '£', emoji: '🇫🇰' },
  'FLOW': { name: 'Flow', symbol: 'FLOW', emoji: '🌊' },
  'FLR': { name: 'Flare', symbol: 'FLR', emoji: '🔥' },
  'FRAX': { name: 'Frax', symbol: 'FRAX', emoji: '❄️' },
  'FRF': { name: 'French Franc', symbol: 'F', emoji: '🇫🇷' },
  'FTT': { name: 'FTX Token', symbol: 'FTT', emoji: '🌟' },
  'GALA': { name: 'Gala', symbol: 'GALA', emoji: '🎪' },
  'GBP': { name: 'British Pound Sterling', symbol: '£', emoji: '🇬🇧' },
  'GEL': { name: 'Georgian Lari', symbol: 'ლ', emoji: '🇬🇪' },
  'GGP': { name: 'Guernsey Pound', symbol: '£', emoji: '🇬🇬' },
  'GHC': { name: 'Ghanaian Cedi (1979-2007)', symbol: '₵', emoji: '🇬🇭' },
  'GHS': { name: 'Ghanaian Cedi', symbol: '₵', emoji: '🇬🇭' },
  'GIP': { name: 'Gibraltar Pound', symbol: '£', emoji: '🇬🇮' },
  'GMD': { name: 'Gambian Dalasi', symbol: 'D', emoji: '🇬🇲' },
  'GMX': { name: 'GMX', symbol: 'GMX', emoji: '📈' },
  'GNF': { name: 'Guinean Franc', symbol: 'Fr', emoji: '🇬🇳' },
  'GNO': { name: 'Gnosis', symbol: 'GNO', emoji: '🦉' },
  'GRD': { name: 'Greek Drachma', symbol: '₯', emoji: '🇬🇷' },
  'GRT': { name: 'The Graph', symbol: 'GRT', emoji: '📊' },
  'GT': { name: 'GateToken', symbol: 'GT', emoji: '🚪' },
  'GTQ': { name: 'Guatemalan Quetzal', symbol: 'Q', emoji: '🇬🇹' },
  'GUSD': { name: 'Gemini Dollar', symbol: 'GUSD', emoji: '♊' },
  'GYD': { name: 'Guyanaese Dollar', symbol: '$', emoji: '🇬🇾' },
  'HBAR': { name: 'Hedera', symbol: 'HBAR', emoji: '🌿' },
  'HKD': { name: 'Hong Kong Dollar', symbol: '$', emoji: '🇭🇰' },
  'HNL': { name: 'Honduran Lempira', symbol: 'L', emoji: '🇭🇳' },
  'HNT': { name: 'Helium', symbol: 'HNT', emoji: '🎈' },
  'HOT': { name: 'Holo', symbol: 'HOT', emoji: '🔥' },
  'HRK': { name: 'Croatian Kuna', symbol: 'kn', emoji: '🇭🇷' },
  'HT': { name: 'Huobi Token', symbol: 'HT', emoji: '🔶' },
  'HTG': { name: 'Haitian Gourde', symbol: 'G', emoji: '🇭🇹' },
  'HUF': { name: 'Hungarian Forint', symbol: 'Ft', emoji: '🇭🇺' },
  'ICP': { name: 'Internet Computer', symbol: 'ICP', emoji: '🌐' },
  'IDR': { name: 'Indonesian Rupiah', symbol: 'Rp', emoji: '🇮🇩' },
  'IEP': { name: 'Irish Pound', symbol: '£', emoji: '🇮🇪' },
  'ILS': { name: 'Israeli New Sheqel', symbol: '₪', emoji: '🇮🇱' },
  'IMP': { name: 'Manx pound', symbol: '£', emoji: '🇮🇲' },
  'IMX': { name: 'Immutable X', symbol: 'IMX', emoji: '🔷' },
  'INJ': { name: 'Injective', symbol: 'INJ', emoji: '💉' },
  'INR': { name: 'Indian Rupee', symbol: '₹', emoji: '🇮🇳' },
  'IQD': { name: 'Iraqi Dinar', symbol: 'ع.د', emoji: '🇮🇶' },
  'IRR': { name: 'Iranian Rial', symbol: '﷼', emoji: '🇮🇷' },
  'ISK': { name: 'Icelandic Krona', symbol: 'kr', emoji: '🇮🇸' },
  'ITL': { name: 'Italian Lira', symbol: 'L', emoji: '🇮🇹' },
  'JEP': { name: 'Jersey Pound', symbol: '£', emoji: '🇯🇪' },
  'JMD': { name: 'Jamaican Dollar', symbol: '$', emoji: '🇯🇲' },
  'JOD': { name: 'Jordanian Dinar', symbol: 'د.ا', emoji: '🇯🇴' },
  'JPY': { name: 'Japanese Yen', symbol: '¥', emoji: '🇯🇵' },
  'KAS': { name: 'Kaspa', symbol: 'KAS', emoji: '👻' },
  'KAVA': { name: 'Kava', symbol: 'KAVA', emoji: '🌋' },
  'KCS': { name: 'KuCoin Token', symbol: 'KCS', emoji: '🔶' },
  'KDA': { name: 'Kadena', symbol: 'KDA', emoji: '🔗' },
  'KES': { name: 'Kenyan Shilling', symbol: 'KSh', emoji: '🇰🇪' },
  'KGS': { name: 'Kyrgystani Som', symbol: 'лв', emoji: '🇰🇬' },
  'KHR': { name: 'Cambodian Riel', symbol: '៛', emoji: '🇰🇭' },
  'KLAY': { name: 'Klaytn', symbol: 'KLAY', emoji: '🏗️' },
  'KMF': { name: 'Comorian Franc', symbol: 'Fr', emoji: '🇰🇲' },
  'KNC': { name: 'Kyber Network Crystal v2', symbol: 'KNC', emoji: '💎' },
  'KPW': { name: 'North Korean Won', symbol: '₩', emoji: '🇰🇵' },
  'KRW': { name: 'South Korean Won', symbol: '₩', emoji: '🇰🇷' },
  'KSM': { name: 'Kusama', symbol: 'KSM', emoji: '🐦' },
  'KWD': { name: 'Kuwaiti Dinar', symbol: 'د.ك', emoji: '🇰🇼' },
  'KYD': { name: 'Cayman Islands Dollar', symbol: '$', emoji: '🇰🇾' },
  'KZT': { name: 'Kazakhstani Tenge', symbol: '₸', emoji: '🇰🇿' },
  'LAK': { name: 'Laotian Kip', symbol: '₭', emoji: '🇱🇦' },
  'LBP': { name: 'Lebanese Pound', symbol: 'ل.ل', emoji: '🇱🇧' },
  'LDO': { name: 'Lido DAO', symbol: 'LDO', emoji: '🌊' },
  'LEO': { name: 'UNUS SED LEO', symbol: 'LEO', emoji: '🦁' },
  'LINK': { name: 'Chainlink', symbol: 'LINK', emoji: '🔗' },
  'LKR': { name: 'Sri Lankan Rupee', symbol: 'Rs', emoji: '🇱🇰' },
  'LRC': { name: 'Loopring', symbol: 'LRC', emoji: '🔄' },
  'LRD': { name: 'Liberian Dollar', symbol: '$', emoji: '🇱🇷' },
  'LSL': { name: 'Lesotho Loti', symbol: 'M', emoji: '🇱🇸' },
  'LTC': { name: 'Litecoin', symbol: 'Ł', emoji: '🥈' },
  'LTL': { name: 'Lithuanian Litas', symbol: 'Lt', emoji: '🇱🇹' },
  'LUF': { name: 'Luxembourgian Franc', symbol: 'F', emoji: '🇱🇺' },
  'LUNA': { name: 'Terra Luna', symbol: 'LUNA', emoji: '🌙' },
  'LUNC': { name: 'Terra Luna Classic', symbol: 'LUNC', emoji: '🌑' },
  'LVL': { name: 'Latvian Lats', symbol: 'Ls', emoji: '🇱🇻' },
  'LYD': { name: 'Libyan Dinar', symbol: 'ل.د', emoji: '🇱🇾' },
  'MAD': { name: 'Moroccan Dirham', symbol: 'د.م.', emoji: '🇲🇦' },
  'MANA': { name: 'Decentraland', symbol: 'MANA', emoji: '🏗️' },
  'MBX': { name: 'MARBLEX', symbol: 'MBX', emoji: '🎮' },
  'MDL': { name: 'Moldovan Leu', symbol: 'L', emoji: '🇲🇩' },
  'MGA': { name: 'Malagasy Ariary', symbol: 'Ar', emoji: '🇲🇬' },
  'MGF': { name: 'Malagasy Franc', symbol: 'Fr', emoji: '🇲🇬' },
  'MINA': { name: 'Mina', symbol: 'MINA', emoji: '🔒' },
  'MKD': { name: 'Macedonian Denar', symbol: 'ден', emoji: '🇲🇰' },
  'MKR': { name: 'Maker', symbol: 'MKR', emoji: '🏭' },
  'MMK': { name: 'Myanma Kyat', symbol: 'Ks', emoji: '🇲🇲' },
  'MNT': { name: 'Mongolian Tugrik', symbol: '₮', emoji: '🇲🇳' },
  'MOP': { name: 'Macanese Pataca', symbol: 'P', emoji: '🇲🇴' },
  'MRO': { name: 'Mauritanian Ouguiya (1973-2017)', symbol: 'UM', emoji: '🇲🇷' },
  'MRU': { name: 'Mauritanian Ouguiya', symbol: 'UM', emoji: '🇲🇷' },
  'MTL': { name: 'Maltese Lira', symbol: '₤', emoji: '🇲🇹' },
  'MUR': { name: 'Mauritian Rupee', symbol: '₨', emoji: '🇲🇺' },
  'MVR': { name: 'Maldivian Rufiyaa', symbol: '.ރ', emoji: '🇲🇻' },
  'MWK': { name: 'Malawian Kwacha', symbol: 'MK', emoji: '🇲🇼' },
  'MXN': { name: 'Mexican Peso', symbol: '$', emoji: '🇲🇽' },
  'MXV': { name: 'Mexican Unidad de Inversion (UDI)', symbol: 'UDI', emoji: '🇲🇽' },
  'MYR': { name: 'Malaysian Ringgit', symbol: 'RM', emoji: '🇲🇾' },
  'MZM': { name: 'Mozambican Metical (1980-2006)', symbol: 'Mt', emoji: '🇲🇿' },
  'MZN': { name: 'Mozambican Metical', symbol: 'Mt', emoji: '🇲🇿' },
  'NAD': { name: 'Namibian Dollar', symbol: '$', emoji: '🇳🇦' },
  'NEAR': { name: 'NEAR Protocol', symbol: 'NEAR', emoji: '🌐' },
  'NEO': { name: 'Neo', symbol: 'NEO', emoji: '🟢' },
  'NEXO': { name: 'Nexo', symbol: 'NEXO', emoji: '🔗' },
  'NFT': { name: 'APENFT', symbol: 'NFT', emoji: '🎨' },
  'NGN': { name: 'Nigerian Naira', symbol: '₦', emoji: '🇳🇬' },
  'NIO': { name: 'Nicaraguan Cordoba', symbol: 'C$', emoji: '🇳🇮' },
  'NLG': { name: 'Dutch Guilder', symbol: 'ƒ', emoji: '🇳🇱' },
  'NOK': { name: 'Norwegian Krone', symbol: 'kr', emoji: '🇳🇴' },
  'NPR': { name: 'Nepalese Rupee', symbol: '₨', emoji: '🇳🇵' },
  'NZD': { name: 'New Zealand Dollar', symbol: '$', emoji: '🇳🇿' },
  'OKB': { name: 'OKB', symbol: 'OKB', emoji: '⭕' },
  'OMR': { name: 'Omani Rial', symbol: 'ر.ع.', emoji: '🇴🇲' },
  'ONE': { name: 'Harmony', symbol: 'ONE', emoji: '🎵' },
  'OP': { name: 'Optimism', symbol: 'OP', emoji: '🔴' },
  'ORDI': { name: 'ORDI', symbol: 'ORDI', emoji: '🟠' },
  'PAB': { name: 'Panamanian Balboa', symbol: 'B/.', emoji: '🇵🇦' },
  'PAXG': { name: 'PAX Gold', symbol: 'PAXG', emoji: '🥇' },
  'PEN': { name: 'Peruvian Nuevo Sol', symbol: 'S/.', emoji: '🇵🇪' },
  'PEPE': { name: 'Pepe', symbol: 'PEPE', emoji: '🐸' },
  'PGK': { name: 'Papua New Guinean Kina', symbol: 'K', emoji: '🇵🇬' },
  'PHP': { name: 'Philippine Peso', symbol: '₱', emoji: '🇵🇭' },
  'PKR': { name: 'Pakistani Rupee', symbol: '₨', emoji: '🇵🇰' },
  'PLN': { name: 'Polish Zloty', symbol: 'zł', emoji: '🇵🇱' },
  'POL': { name: 'Polygon', symbol: 'POL', emoji: '🟣' },
  'PTE': { name: 'Portuguese Escudo', symbol: '₤', emoji: '🇵🇹' },
  'PYG': { name: 'Paraguayan Guarani', symbol: '₲', emoji: '🇵🇾' },
  'QAR': { name: 'Qatari Rial', symbol: 'ر.ق', emoji: '🇶🇦' },
  'QNT': { name: 'Quant', symbol: 'QNT', emoji: '⚡' },
  'QTUM': { name: 'Qtum', symbol: 'QTUM', emoji: '🔷' },
  'ROL': { name: 'Romanian Leu (1952-2006)', symbol: 'L', emoji: '🇷🇴' },
  'RON': { name: 'Romanian Leu', symbol: 'lei', emoji: '🇷🇴' },
  'RPL': { name: 'Rocket Pool', symbol: 'RPL', emoji: '🚀' },
  'RSD': { name: 'Serbian Dinar', symbol: 'дин.', emoji: '🇷🇸' },
  'RUB': { name: 'Russian Ruble', symbol: '₽', emoji: '🇷🇺' },
  'RUNE': { name: 'THORChain', symbol: 'RUNE', emoji: '⚡' },
  'RVN': { name: 'Ravencoin', symbol: 'RVN', emoji: '🐦' },
  'RWF': { name: 'Rwandan Franc', symbol: 'Fr', emoji: '🇷🇼' },
  'SAND': { name: 'The Sandbox', symbol: 'SAND', emoji: '🏖️' },
  'SAR': { name: 'Saudi Riyal', symbol: 'ر.س', emoji: '🇸🇦' },
  'SBD': { name: 'Solomon Islands Dollar', symbol: '$', emoji: '🇸🇧' },
  'SCR': { name: 'Seychellois Rupee', symbol: '₨', emoji: '🇸🇨' },
  'SDD': { name: 'Sudanese Dinar (1992-2007)', symbol: 'د.س.', emoji: '🇸🇩' },
  'SDG': { name: 'Sudanese Pound', symbol: 'ج.س.', emoji: '🇸🇩' },
  'SEK': { name: 'Swedish Krona', symbol: 'kr', emoji: '🇸🇪' },
  'SGD': { name: 'Singapore Dollar', symbol: '$', emoji: '🇸🇬' },
  'SHIB': { name: 'Shiba Inu', symbol: 'SHIB', emoji: '🐕' },
  'SHP': { name: 'Saint Helena Pound', symbol: '£', emoji: '🇸🇭' },
  'SIT': { name: 'Slovenian Tolar', symbol: 'SIT', emoji: '🇸🇮' },
  'SKK': { name: 'Slovak Koruna', symbol: 'Sk', emoji: '🇸🇰' },
  'SLE': { name: 'Sierra Leonean Leone', symbol: 'Le', emoji: '🇸🇱' },
  'SLL': { name: 'Sierra Leonean Leone (1964-2022)', symbol: 'Le', emoji: '🇸🇱' },
  'SNX': { name: 'Synthetix', symbol: 'SNX', emoji: '🔗' },
  'SOL': { name: 'Solana', symbol: 'SOL', emoji: '☀️' },
  'SOS': { name: 'Somali Shilling', symbol: 'Sh', emoji: '🇸🇴' },
  'SPL': { name: 'Spell Token', symbol: 'SPELL', emoji: '🪄' },
  'SRD': { name: 'Surinamese Dollar', symbol: '$', emoji: '🇸🇷' },
  'SRG': { name: 'Surinamese Guilder', symbol: 'ƒ', emoji: '🇸🇷' },
  'STD': { name: 'Sao Tome and Principe Dobra (1977-2017)', symbol: 'Db', emoji: '🇸🇹' },
  'STN': { name: 'Sao Tome and Principe Dobra', symbol: 'Db', emoji: '🇸🇹' },
  'STX': { name: 'Stacks', symbol: 'STX', emoji: '📚' },
  'SUI': { name: 'Sui', symbol: 'SUI', emoji: '🌊' },
  'SVC': { name: 'Salvadoran Colon', symbol: '¢', emoji: '🇸🇻' },
  'SYP': { name: 'Syrian Pound', symbol: 'ل.س', emoji: '🇸🇾' },
  'SZL': { name: 'Swazi Lilangeni', symbol: 'E', emoji: '🇸🇿' },
  'THB': { name: 'Thai Baht', symbol: '฿', emoji: '🇹🇭' },
  'THETA': { name: 'THETA', symbol: 'THETA', emoji: '📺' },
  'TJS': { name: 'Tajikistani Somoni', symbol: 'SM', emoji: '🇹🇯' },
  'TMM': { name: 'Turkmenistani Manat (1993-2009)', symbol: 'T', emoji: '🇹🇲' },
  'TMT': { name: 'Turkmenistani Manat', symbol: 'T', emoji: '🇹🇲' },
  'TND': { name: 'Tunisian Dinar', symbol: 'د.ت', emoji: '🇹🇳' },
  'TON': { name: 'Toncoin', symbol: 'TON', emoji: '💎' },
  'TOP': { name: 'Tongan Paanga', symbol: 'T$', emoji: '🇹🇴' },
  'TRL': { name: 'Turkish Lira (1922-2005)', symbol: '₤', emoji: '🇹🇷' },
  'TRX': { name: 'TRON', symbol: 'TRX', emoji: '🔥' },
  'TRY': { name: 'Turkish Lira', symbol: '₺', emoji: '🇹🇷' },
  'TTD': { name: 'Trinidad and Tobago Dollar', symbol: '$', emoji: '🇹🇹' },
  'TUSD': { name: 'TrueUSD', symbol: 'TUSD', emoji: '✅' },
  'TVD': { name: 'Tuvalu Dollar', symbol: '$', emoji: '🇹🇻' },
  'TWD': { name: 'New Taiwan Dollar', symbol: '$', emoji: '🇹🇼' },
  'TWT': { name: 'Trust Wallet Token', symbol: 'TWT', emoji: '🔐' },
  'TZS': { name: 'Tanzanian Shilling', symbol: 'Sh', emoji: '🇹🇿' },
  'UAH': { name: 'Ukrainian Hryvnia', symbol: '₴', emoji: '🇺🇦' },
  'UGX': { name: 'Ugandan Shilling', symbol: 'UGX', emoji: '🇺🇬' },
  'UNI': { name: 'Uniswap', symbol: 'UNI', emoji: '🦄' },
  'USD': { name: 'US Dollar', symbol: '$', emoji: '🇺🇸' },
  'USDC': { name: 'USD Coin', symbol: 'USDC', emoji: '🔵' },
  'USDD': { name: 'USDD', symbol: 'USDD', emoji: '💚' },
  'USDP': { name: 'Pax Dollar', symbol: 'USDP', emoji: '🟢' },
  'USDT': { name: 'Tether', symbol: 'USDT', emoji: '🟢' },
  'UYU': { name: 'Uruguayan Peso', symbol: '$', emoji: '🇺🇾' },
  'UZS': { name: 'Uzbekistan Som', symbol: 'лв', emoji: '🇺🇿' },
  'VAL': { name: 'Euro', symbol: '€', emoji: '🇻🇦' },
  'VEB': { name: 'Venezuelan Bolívar (1871-2008)', symbol: 'Bs', emoji: '🇻🇪' },
  'VED': { name: 'Venezuelan Bolívar Soberano', symbol: 'Bs.S', emoji: '🇻🇪' },
  'VEF': { name: 'Venezuelan Bolívar (2008-2018)', symbol: 'Bs.F', emoji: '🇻🇪' },
  'VES': { name: 'Venezuelan Bolívar Soberano', symbol: 'Bs.S', emoji: '🇻🇪' },
  'VET': { name: 'VeChain', symbol: 'VET', emoji: '💚' },
  'VND': { name: 'Vietnamese Dong', symbol: '₫', emoji: '🇻🇳' },
  'VUV': { name: 'Vanuatu Vatu', symbol: 'Vt', emoji: '🇻🇺' },
  'WAVES': { name: 'Waves', symbol: 'WAVES', emoji: '🌊' },
  'WEMIX': { name: 'WEMIX', symbol: 'WEMIX', emoji: '🎮' },
  'WOO': { name: 'WOO Network', symbol: 'WOO', emoji: '🐺' },
  'WST': { name: 'Samoan Tala', symbol: 'T', emoji: '🇼🇸' },
  'XAF': { name: 'CFA Franc BEAC', symbol: 'Fr', emoji: '🌍' },
  'XAG': { name: 'Silver Ounce', symbol: 'XAG', emoji: '🥈' },
  'XAU': { name: 'Gold Ounce', symbol: 'XAU', emoji: '🥇' },
  'XAUT': { name: 'Tether Gold', symbol: 'XAUT', emoji: '🥇' },
  'XBT': { name: 'Bitcoin', symbol: '₿', emoji: '🟠' },
  'XCD': { name: 'East Caribbean Dollar', symbol: '$', emoji: '🏝️' },
  'XCG': { name: 'California Gold', symbol: 'XCG', emoji: '🥇' },
  'XCH': { name: 'Chia', symbol: 'XCH', emoji: '🌱' },
  'XDC': { name: 'XDC Network', symbol: 'XDC', emoji: '🌐' },
  'XDR': { name: 'Special Drawing Rights', symbol: 'XDR', emoji: '🏦' },
  'XEC': { name: 'eCash', symbol: 'XEC', emoji: '💰' },
  'XEM': { name: 'NEM', symbol: 'XEM', emoji: '🌟' },
  'XLM': { name: 'Stellar', symbol: 'XLM', emoji: '⭐' },
  'XMR': { name: 'Monero', symbol: 'XMR', emoji: '🔒' },
  'XOF': { name: 'CFA Franc BCEAO', symbol: 'Fr', emoji: '🌍' },
  'XPD': { name: 'Palladium Ounce', symbol: 'XPD', emoji: '⚪' },
  'XPF': { name: 'CFP Franc', symbol: 'Fr', emoji: '🏝️' },
  'XPT': { name: 'Platinum Ounce', symbol: 'XPT', emoji: '⚪' },
  'XRP': { name: 'XRP', symbol: 'XRP', emoji: '💧' },
  'XTZ': { name: 'Tezos', symbol: 'XTZ', emoji: '🔷' },
  'YER': { name: 'Yemeni Rial', symbol: '﷼', emoji: '🇾🇪' },
  'ZAR': { name: 'South African Rand', symbol: 'R', emoji: '🇿🇦' },
  'ZEC': { name: 'Zcash', symbol: 'ZEC', emoji: '🛡️' },
  'ZIL': { name: 'Zilliqa', symbol: 'ZIL', emoji: '⚡' },
  'ZMK': { name: 'Zambian Kwacha (1968-2012)', symbol: 'ZK', emoji: '🇿🇲' },
  'ZMW': { name: 'Zambian Kwacha', symbol: 'ZK', emoji: '🇿🇲' },
  'ZWD': { name: 'Zimbabwean Dollar (1980-2008)', symbol: '$', emoji: '🇿🇼' },
  'ZWG': { name: 'Zimbabwean Gold', symbol: 'ZWG', emoji: '🥇' },
  'ZWL': { name: 'Zimbabwean Dollar (2009-2015)', symbol: '$', emoji: '🇿🇼' }
};

// Get all supported currency codes
export const getAllCurrencies = (): string[] => {
  return Object.keys(CURRENCY_INFO).sort();
};

// Get currency display text with emoji and ISO code first
export const getCurrencyDisplayText = (code: string): string => {
  const info = CURRENCY_INFO[code];
  if (!info) return code;
  return `${info.emoji || '💰'} ${code} - ${info.name}`;
};

// Filter currencies based on search term (ISO code or name)
export const filterCurrencies = (searchTerm: string): string[] => {
  const lowerSearch = searchTerm.toLowerCase();
  return getAllCurrencies().filter(currency => {
    const info = CURRENCY_INFO[currency];
    return (
      currency.toLowerCase().includes(lowerSearch) ||
      info.name.toLowerCase().includes(lowerSearch)
    );
  });
};

// Get popular/commonly used currencies for the main selector
export const getPopularCurrencies = (): string[] => {
  return [
    'USD', 'EUR', 'GBP', 'JPY', 'AUD', 'CAD', 'CHF', 'CNY', 'SEK', 'NZD',
    'SAR', 'AED', 'EGP', 'QAR', 'KWD', 'BHD', 'OMR', 'JOD',
    'BTC', 'ETH', 'USDT', 'USDC', 'BNB'
  ];
};  